import type { Translate } from 'next-translate';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Container } from '@/components/elements/Container';
import { MyImage } from '@/components/elements/MyImage';
import { Button } from '@/components/UI/Button';
import { useEmailFunnelFlow, useTranslationWithLocale } from '@/hooks';
import { CookiesStorageService } from '@/services';
import { type HeroWithLink } from '@/types';
import { LANDING_COOKIE_STORAGE } from '@/constants';
import ClearIcon from '@/public/icons/cross.svg';
import PasteIcon from '@/public/icons/paste.svg';
import bgMobile from '@/public/images/landing/backgrounds/bg-abstract-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg-abstract.webp';
import { EmailPopup } from '../EmailPopup';
import styles from './HeroWebLink.module.scss';

interface Props {
	data: HeroWithLink;
	t: Translate;
	funnelLinkService?: string;
}

interface FormValues {
	url: string;
}

const MAX_URL_LENGTH = 600;

export const HeroWebLink: React.FC<Props> = ({
	data: configData,
	t,
	funnelLinkService,
}) => {
	const { title, description, images } = configData;

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		resetField,
		formState: { errors },
	} = useForm<FormValues>({ mode: 'onChange', defaultValues: { url: '' } });

	const { t: tCommon } = useTranslationWithLocale('common');

	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const { ref: rhfInputRef, ...urlRegister } = register('url', {
		required: true,
		maxLength: MAX_URL_LENGTH,
	});

	const urlValue = watch('url');
	const hasValue = Boolean(urlValue);

	const placeholderKey = 'heroData.inputPlaceholder';
	const placeholderTranslated = t(placeholderKey);
	const inputPlaceholder =
		placeholderTranslated && placeholderTranslated !== placeholderKey
			? placeholderTranslated
			: tCommon('pasteALink');

	const { open: openEmailPopup, popupProps } = useEmailFunnelFlow({
		funnelLinkService,
		t,
		tCommon,
	});

	const onSubmit = ({ url }: FormValues) => {
		CookiesStorageService.set(LANDING_COOKIE_STORAGE.LINK_FROM_LANDING, url);
		openEmailPopup();
	};

	const handlePaste = async () => {
		inputRef.current?.focus();

		if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
			return;
		}
		try {
			const text = await navigator.clipboard.readText();
			if (text) {
				setValue('url', text.slice(0, MAX_URL_LENGTH), {
					shouldValidate: true,
					shouldDirty: true,
					shouldTouch: true,
				});
			}
		} catch {
			// clipboard read denied — silently ignore
		}
	};

	const handleClear = () => {
		resetField('url');
	};

	const ratingImages = images.filter(
		img => img.id !== 'left' && img.id !== 'right',
	);

	return (
		<section className={styles['hero']}>
			<MyImage
				imageProps={{
					src: bgMobile,
					alt: 'background image',
					title: `${t(title)}`,
					priority: true,
					fetchPriority: 'low',
				}}
				settings={{
					classes: {
						picture: styles['hero__background'],
						image: styles['hero__background-img'],
					},
				}}
			>
				<source srcSet={bg.src} media='(min-width: 1023px)' />
			</MyImage>

			<Container className={styles['hero__container']}>
				<div className={styles['hero__info']}>
					<h1 className={styles['hero__title']}>{t(title)}</h1>
					<p className={styles['hero__subtitle']}>{t(description)}</p>
				</div>

				<div className={styles['hero__link-wrapper']}>
					<form
						className={`${styles['hero__form']} ${
							errors.url ? styles['hero__form_error'] : ''
						}`}
						onSubmit={handleSubmit(onSubmit)}
						noValidate
					>
						<div className={`${styles['hero__form-dash']}`}>
							<div
								className={`${styles['hero__field']} ${
									hasValue ? styles['hero__field_filled'] : ''
								}`}
							>
								<input
									type='url'
									inputMode='url'
									maxLength={MAX_URL_LENGTH}
									placeholder={inputPlaceholder}
									className={styles['hero__input']}
									{...urlRegister}
									ref={el => {
										rhfInputRef(el);
										inputRef.current = el;
									}}
								/>
								{hasValue ? (
									<button
										type='button'
										aria-label='Clear'
										className={styles['hero__clear']}
										onClick={handleClear}
									>
										<ClearIcon className={styles['hero__clear-icon']} />
									</button>
								) : (
									<button
										type='button'
										className={styles['hero__paste']}
										onClick={handlePaste}
									>
										<PasteIcon className={styles['hero__paste-icon']} />
										<span>{tCommon('paste')}</span>
									</button>
								)}
							</div>

							<Button
								as='button'
								type='submit'
								preset='primary'
								text={tCommon('download')}
								size='medium'
								disabled={!hasValue}
								classes={{
									button: styles['hero__submit'],
								}}
							/>
						</div>
					</form>
				</div>
			</Container>

			<EmailPopup {...popupProps} />
		</section>
	);
};
